import { cs } from '../../../lib/currency';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Users, CalendarClock, Banknote, Send, CreditCard, Download, Mail,
  Ban, DollarSign, Trash2, RefreshCw, TicketPercent,
} from 'lucide-react';
import { formatTime } from './utils';
import PaymentSyncButton from '../PaymentSyncButton';
import NetAmount from '../../shared/NetAmount';

/**
 * Booking Details modal (opened when admin clicks a booking tile in the
 * calendar). Bundles every action available on a single booking: payment,
 * invoice, recurring-series edit/cancel, refund, cancel, reschedule.
 */
export const BookingDetailsModal = ({
  open,
  onOpenChange,
  booking,
  lanes,
  onOpenPayment,
  onDownloadInvoice,
  onEmailInvoice,
  onOpenEditSeries,
  onOpenCancelSeries,
  onOpenCancelSingle,
  onOpenRefund,
  onOpenDiscount,
  onReschedule,
  onRefresh,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="booking-details-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-500" />
          Booking Details
          {booking?.recurrence_id && (
            <Badge
              variant="outline"
              className="ml-2 text-purple-700 border-purple-300 bg-purple-50 text-xs"
              data-testid="recurring-badge"
            >
              <CalendarClock className="h-3 w-3 mr-1" />
              Recurring
            </Badge>
          )}
        </DialogTitle>
      </DialogHeader>

      {booking && (
        <div className="space-y-4 py-2">
          <div className="space-y-3">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-slate-500">Customer</p>
                <p className="font-medium text-slate-900">{booking.customer_name}</p>
              </div>
              <div className="flex items-center gap-1">
                {booking.payment_status !== 'paid' && booking.payment_session_id && (
                  <PaymentSyncButton
                    entityType="lane_booking"
                    entityId={booking.id}
                    onUpdated={() => { onOpenChange && onOpenChange(false); onRefresh && onRefresh(); }}
                    testId={`sync-lane-${booking.id}`}
                  />
                )}
                <Badge className={
                  booking.payment_status === 'paid'
                    ? 'bg-green-100 text-green-700 border-green-200'
                    : booking.payment_session_id
                      ? 'bg-blue-100 text-blue-700 border-blue-200'
                      : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                }>
                  {booking.payment_status === 'paid'
                    ? 'Paid'
                    : booking.payment_session_id
                      ? 'Payment Pending'
                      : 'Unpaid'}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="text-sm text-slate-700">{booking.customer_email}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Phone</p>
                <p className="text-sm text-slate-700">{booking.customer_phone || 'N/A'}</p>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Lane</p>
                  <p className="font-medium text-slate-900">{booking.lane_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date</p>
                  <p className="font-medium text-slate-900">{booking.date}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Time</p>
                <p className="font-medium text-slate-900">
                  {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Price</p>
                <p className="font-medium text-slate-900">
                  <NetAmount
                    amount={booking.price}
                    refundedAmount={booking.refunded_amount}
                    refundStatus={booking.refund_status}
                    testId="booking-net-amount"
                  />
                </p>
              </div>
            </div>

            {booking.equipment_rentals && booking.equipment_rentals.length > 0 && (
              <div className="border-t border-slate-100 pt-3">
                <p className="text-sm text-slate-500 mb-1">Equipment rentals</p>
                <div className="space-y-1">
                  {booking.equipment_rentals.map((r, idx) => (
                    <div key={`${r.equipment_id}-${idx}`} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">
                        {r.quantity}x {r.equipment_name}
                      </span>
                      <span className="text-slate-500">{cs()}{Number(r.subtotal || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {booking.notes && (
              <div className="border-t border-slate-100 pt-3">
                <p className="text-sm text-slate-500">Notes</p>
                <p className="text-sm text-slate-700">{booking.notes}</p>
              </div>
            )}

            {booking.rescheduled_at && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-600 font-medium">This booking was rescheduled</p>
                {booking.reschedule_reason && (
                  <p className="text-xs text-blue-500 mt-1">Reason: {booking.reschedule_reason}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <DialogFooter className="flex-col gap-4 sm:flex-col">
        {booking?.payment_status !== 'paid' && (
          <div className="w-full space-y-2">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Payment Options</p>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpenPayment(booking, 'record')} className="text-green-600 border-green-200 hover:bg-green-50 flex-col h-auto py-2" data-testid="record-payment-btn">
                <Banknote className="h-4 w-4 mb-1" />
                <span className="text-xs">Record Payment</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => onOpenPayment(booking, 'send_link')} className="text-blue-600 border-blue-200 hover:bg-blue-50 flex-col h-auto py-2" data-testid="send-payment-link-btn">
                <Send className="h-4 w-4 mb-1" />
                <span className="text-xs">Send Link</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => onOpenPayment(booking, 'process_card')} className="text-purple-600 border-purple-200 hover:bg-purple-50 flex-col h-auto py-2" data-testid="process-card-btn">
                <CreditCard className="h-4 w-4 mb-1" />
                <span className="text-xs">Process Card</span>
              </Button>
            </div>
          </div>
        )}

        <div className="w-full space-y-2">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Invoice</p>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" onClick={() => onDownloadInvoice(booking?.id)} data-testid="download-invoice-btn">
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
            <Button variant="outline" size="sm" onClick={() => onEmailInvoice(booking?.id)} data-testid="email-invoice-btn">
              <Mail className="h-4 w-4 mr-1" />
              Email
            </Button>
          </div>
        </div>

        {booking?.recurrence_id && (
          <div className="w-full space-y-2">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Recurring Series</p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => onOpenEditSeries({ ...booking, lane_name: lanes.find((l) => l.id === booking.lane_id)?.name })}
                className="text-purple-600 border-purple-200 hover:bg-purple-50"
                data-testid="details-edit-series-btn"
              >
                <CalendarClock className="h-4 w-4 mr-1" />
                Edit Series
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => onOpenCancelSeries(booking)}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="details-cancel-series-btn"
              >
                <Ban className="h-4 w-4 mr-1" />
                Cancel Series
              </Button>
            </div>
            <p className="text-[11px] text-slate-400">
              Edit = change this + future occurrences. Cancel = cancel this + all future occurrences.
            </p>
          </div>
        )}

        <div className="w-full space-y-2">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Discount</p>
          <Button
            variant="outline" size="sm"
            onClick={() => onOpenDiscount && onOpenDiscount(booking)}
            className="w-full text-emerald-700 border-emerald-200 hover:bg-emerald-50"
            data-testid="details-discount-btn"
          >
            <TicketPercent className="h-4 w-4 mr-1" />
            Apply / Manage Discount
          </Button>
        </div>

        {booking?.payment_status === 'paid' && (
          <div className="w-full space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Refunds</p>
              {booking?.refunded_amount > 0 && (
                <span className="text-[11px] text-slate-500">
                  {cs()}{Number(booking.refunded_amount).toFixed(2)} already refunded
                  {booking.refund_status === 'fully_refunded' && ' (fully)'}
                </span>
              )}
            </div>
            <Button
              variant="outline" size="sm"
              onClick={() => onOpenRefund(booking)}
              disabled={booking?.refund_status === 'fully_refunded'}
              className="w-full text-amber-700 border-amber-200 hover:bg-amber-50"
              data-testid="details-refund-btn"
            >
              <DollarSign className="h-4 w-4 mr-1" />
              {booking?.refund_status === 'fully_refunded' ? 'Fully Refunded' : 'Issue Refund'}
            </Button>
          </div>
        )}

        <div className="w-full flex gap-2 justify-between pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenCancelSingle(booking)}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="details-cancel-single-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button onClick={onReschedule} className="bg-blue-500 hover:bg-blue-600" data-testid="details-reschedule-btn">
              <RefreshCw className="h-4 w-4 mr-1" />
              Reschedule
            </Button>
          </div>
        </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default BookingDetailsModal;
